import React, { useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Tooltip,
  useTheme,
} from '@mui/material';
import { X, ZoomIn, ZoomOut, Download, ExternalLink, ImageOff } from 'lucide-react';

/**
 * ImagePreviewDialog - High-contrast lightbox modal for previewing images, logos, banners, attachments, and KYC documents.
 */
export default function ImagePreviewDialog({
  open,
  onClose,
  src,
  alt = 'Preview image',
  title,
  subtitle,
  downloadable = true,
}) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [zoom, setZoom] = useState(1);

  const handleClose = () => {
    setZoom(1);
    setLoading(true);
    setError(false);
    if (onClose) onClose();
  };

  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.35, 2.5));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.35, 0.7));
  const handleResetZoom = () => setZoom(1);

  const handleDownload = () => {
    if (!src) return;
    const a = document.createElement('a');
    a.href = src;
    a.download = alt || 'image-preview';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.88)',
            backdropFilter: 'blur(8px)',
          },
        },
        paper: {
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
            maxHeight: '92vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            m: { xs: 1, sm: 2 },
          },
        },
      }}
    >
      {/* Top Floating Control Bar */}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 1.5, sm: 2.5 },
          py: 1.25,
          mb: 1.5,
          borderRadius: 2,
          bgcolor: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.12)',
          color: '#FFFFFF',
          zIndex: 10,
        }}
      >
        <Box sx={{ minWidth: 0, pr: 2 }}>
          {title && (
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ color: '#FFFFFF', fontSize: '0.95rem' }}>
              {title}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', display: 'block' }} noWrap>
              {subtitle}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
          {!error && (
            <>
              <Tooltip title="Zoom in">
                <IconButton
                  size="small"
                  onClick={handleZoomIn}
                  sx={{ color: '#FFFFFF', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' } }}
                  aria-label="Zoom in"
                >
                  <ZoomIn size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Zoom out">
                <IconButton
                  size="small"
                  onClick={handleZoomOut}
                  sx={{ color: '#FFFFFF', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' } }}
                  aria-label="Zoom out"
                >
                  <ZoomOut size={18} />
                </IconButton>
              </Tooltip>
              {zoom !== 1 && (
                <Tooltip title="Reset zoom">
                  <IconButton
                    size="small"
                    onClick={handleResetZoom}
                    sx={{ color: '#FFFFFF', fontSize: '0.75rem', px: 1, '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' } }}
                  >
                    100%
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}

          {src && (
            <Tooltip title="Open full size in new tab">
              <IconButton
                size="small"
                component="a"
                href={src}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#FFFFFF', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' } }}
                aria-label="Open image in new tab"
              >
                <ExternalLink size={18} />
              </IconButton>
            </Tooltip>
          )}

          {downloadable && src && !error && (
            <Tooltip title="Download image">
              <IconButton
                size="small"
                onClick={handleDownload}
                sx={{ color: '#FFFFFF', '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.15)' } }}
                aria-label="Download image"
              >
                <Download size={18} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Close (Esc)">
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{
                color: '#FFFFFF',
                bgcolor: 'rgba(255, 255, 255, 0.12)',
                ml: 1,
                '&:hover': { bgcolor: 'rgba(239, 68, 68, 0.8)' },
              }}
              aria-label="Close image preview"
            >
              <X size={18} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Main Image Stage */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          maxHeight: 'calc(85vh - 70px)',
          overflow: 'hidden',
          borderRadius: 2.5,
          bgcolor: 'rgba(0, 0, 0, 0.5)',
          p: { xs: 1, sm: 2 },
        }}
      >
        {loading && !error && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#FFFFFF',
              gap: 1.5,
            }}
          >
            <CircularProgress color="inherit" size={36} />
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              Loading high-resolution asset...
            </Typography>
          </Box>
        )}

        {error ? (
          <Box
            sx={{
              py: 8,
              px: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: 'rgba(255, 255, 255, 0.8)',
              gap: 1.5,
              textAlign: 'center',
            }}
          >
            <ImageOff size={44} strokeWidth={1.5} color={theme.palette.error.light} />
            <Typography variant="subtitle1" fontWeight={700}>
              Unable to load preview
            </Typography>
            <Typography variant="caption" sx={{ maxWidth: 320, color: 'rgba(255, 255, 255, 0.6)' }}>
              The file could not be found or access has expired.
            </Typography>
          </Box>
        ) : (
          <Box
            component="img"
            src={src}
            alt={alt}
            onLoad={() => setLoading(false)}
            onError={() => {
              setLoading(false);
              setError(true);
            }}
            sx={{
              maxWidth: '100%',
              maxHeight: 'calc(85vh - 90px)',
              objectFit: 'contain',
              borderRadius: 1.5,
              transform: `scale(${zoom})`,
              transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: zoom > 1 ? 'grab' : 'zoom-in',
              boxShadow: '0 20px 50px rgba(0, 0, 0, 0.6)',
            }}
            onClick={() => (zoom === 1 ? handleZoomIn() : handleResetZoom())}
          />
        )}
      </Box>
    </Dialog>
  );
}
